/**
 * <analytics-drill-records> — the business records behind a figure.
 *
 * THIS IS WHERE MEASUREMENT STOPS AND BOOKKEEPING BEGINS, and the panel says
 * so: every other body sums, derives or refuses, and this one asserts identity.
 * A row here is a document an operator can open one destination away — an
 * order with an invoice number, a ticket with an admission — which is exactly
 * why the privacy wall stands at the aggregate and not here. An aggregate does
 * not open into a visitor; an order was never an aggregate.
 *
 * THE BOUND IS PRINTED, ALWAYS. A day of a busy show has more orders than a
 * reader scans, so the page is bounded — and a bounded list that stated no
 * bound would read as the whole set, which is the one thing a record list must
 * never imply. The tickets of one order are the deliberate exception: their
 * bound says "all of them" because the panel does state them all, which is
 * what makes the list itself windowed rather than paged. A block order carries
 * up to `MAX_TICKETS_PER_ORDER` of them, so `virtualize()` renders the ones in
 * view and `repeat()` would build every one — the same choice
 * `order-detail.view.ts` makes over this same set.
 *
 * A ROW'S FIGURE IS ITS HANDLE, where it has one. An order's total opens its
 * tickets; a ticket's admission is a time and a door, which is not a quantity,
 * so that row carries no figure and no rule to press.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import "@mhmo91/schmancy/list";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { virtualize } from "@mhmo91/schmancy/virtualizer";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import "../analytics-figure.view.ts";
import type { RecordsAnswer } from "./drill.state.ts";
import { phraseText } from "./drill.state.ts";

/** One row's height, summed from its own typescale tokens rather than
 *  measured in a browser. `virtualize()` sums every offset and the whole
 *  scroll length out of it and never re-measures a rendered row, so any
 *  error here is `(real - this) x rows scrolled past` — it grows with depth
 *  and shows as drift at the end of a long list, never as a thrown error. A row is its `py-2` inset (16px)
 *  plus the headline's own `body/md` line (20px), the `gap-0.5` under it
 *  (2px), its `label/xs` detail line (14px) and the hairline between rows
 *  (1px). The aside beside it — a status word, and an order's own figure —
 *  never reaches that, so the left column alone sets the height.
 *
 *  Every one of those lines is `truncate`d, which is what keeps the numbers
 *  constant: an untruncated buyer name or German type name wraps at panel
 *  width and every offset below it is computed from a height that row no
 *  longer has. */
const ROW_PX = 16 + 20 + 2 + 14 + 1;

/** A row carrying an admission line — the ticket rows all do — is one more
 *  `gap-0.5` + `label/xs` step taller. */
const NOTE_LINE_PX = 2 + 14;

@schmancyElement("analytics-drill-records")
export class AnalyticsDrillRecords extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	@property({ attribute: false }) answer!: RecordsAnswer;

	override render() {
		return html`
			<div class="flex flex-col gap-4">
				<schmancy-typography
					type="body"
					token="sm"
					class="text-surface-onVariant"
				>
					${phraseText(this.answer.lead, this)}
				</schmancy-typography>

				<!-- The rows own this scrollport, and the lead and the bound sit
				     outside it: the window is computed from the scroller's own
				     scrollTop as though the list started at the scroller's top
				     edge, so flow content above the rows inside the same scroller
				     would offset every one of them. -->
				<div ${overflowWithin()} class="max-h-[60dvh] min-h-0">
					${virtualize(
						this.answer.rows,
						(row) => row.id,
						(row) => html`
							<div
								class="flex items-baseline justify-between gap-3 border-b border-outline-variant py-2"
							>
								<div class="flex flex-col gap-0.5 min-w-0">
									<schmancy-typography type="body" token="md" class="truncate">
										${row.headline}
									</schmancy-typography>
									<schmancy-typography
										type="label"
										token="xs"
										class="truncate text-surface-onVariant"
									>
										${phraseText(row.detail, this)}
									</schmancy-typography>
									${when(
										row.note !== null,
										() => html`
											<schmancy-typography
												type="label"
												token="xs"
												class="truncate text-surface-onVariant"
											>
												${phraseText(row.note ?? { text: "" }, this)}
											</schmancy-typography>
										`,
									)}
								</div>
								<div class="flex items-baseline gap-3 shrink-0">
									<schmancy-typography
										type="label"
										token="xs"
										class="text-surface-onVariant"
									>
										${phraseText(row.status, this)}
									</schmancy-typography>
									${when(
										row.amountCents !== null,
										() => html`
											<analytics-figure
												size="inline"
												.label=${row.headline}
												.value=${row.amountCents ?? 0}
												unit="money"
												.node=${row.open}
											></analytics-figure>
										`,
									)}
								</div>
							</div>
						`,
						{
							estimateSize: (index) =>
								this.answer.rows[index]?.note == null
									? ROW_PX
									: ROW_PX + NOTE_LINE_PX,
						},
					)}
				</div>

				<schmancy-typography
					type="label"
					token="sm"
					class="text-surface-onVariant"
				>
					${phraseText(this.answer.bound, this)}
				</schmancy-typography>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-drill-records": AnalyticsDrillRecords;
	}
}
